import { t } from "@lingui/macro";
import { FilesIcon, FilePlusIcon, LinkedinLogoIcon, SquaresFourIcon } from "@phosphor-icons/react";
import { cn } from "@reactive-resume/utils";
import type { ReactNode } from "react";

import { preloadOcrEngine } from "@/client/services/ocr/tesseract";
import { useDialog } from "@/client/stores/dialog";

type ResumeCreateCardProps = {
  onCreate: () => void;
  onUseTemplate: () => void;
  className?: string;
};

type ResumeCreateActionProps = {
  label: string;
  description?: string;
  icon: ReactNode;
  onClick: () => void;
  onMouseEnter?: () => void;
};

const ResumeCreateAction = ({
  label,
  description,
  icon,
  onClick,
  onMouseEnter,
}: ResumeCreateActionProps) => (
  <button
    type="button"
    onClick={onClick}
    onMouseEnter={onMouseEnter}
    className={cn(
      "group flex min-h-[54px] w-full items-center gap-3 border-b border-slate-200 px-3.5 text-left",
      "text-indigo-950 transition-colors duration-200 last:border-b-0 hover:bg-indigo-50/70",
      "focus-visible:relative focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2",
      "focus-visible:ring-inset focus-visible:ring-indigo-500 sm:min-h-[58px] sm:px-4",
    )}
  >
    <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-indigo-50 text-indigo-600 transition-colors group-hover:bg-indigo-100 sm:size-9">
      {icon}
    </span>
    <span className="min-w-0 py-2 text-xs font-semibold sm:text-[13px]">
      {label}
      {description && (
        <span className="mt-1 block text-[10px] font-normal leading-4 text-slate-500">
          {description}
        </span>
      )}
    </span>
  </button>
);

export const ResumeCreateCard = ({ onCreate, onUseTemplate, className }: ResumeCreateCardProps) => {
  const linkedinImportDialog = useDialog("import-linkedin");
  const documentDialog = useDialog("import-ocr");

  return (
    <article
      className={cn(
        "overflow-hidden rounded-2xl border border-dashed border-indigo-400 bg-white shadow-[0_7px_20px_rgba(15,23,42,0.04)]",
        className,
      )}
    >
      <div className="flex min-h-[216px] flex-col sm:min-h-[232px]">
        <ResumeCreateAction
          label="Nouveau CV"
          onClick={onCreate}
          icon={<FilePlusIcon aria-hidden size={19} weight="duotone" />}
        />
        <ResumeCreateAction
          label="Utiliser un modèle"
          onClick={onUseTemplate}
          icon={<SquaresFourIcon aria-hidden size={19} weight="duotone" />}
        />
        <ResumeCreateAction
          label="Importer depuis LinkedIn"
          onClick={() => linkedinImportDialog.open("create")}
          icon={<LinkedinLogoIcon aria-hidden size={19} weight="fill" />}
        />
        <ResumeCreateAction
          label={t`Importer un CV`}
          description={t`PDF (.pdf), Word (.docx) ou image (.jpg, .jpeg, .png, .webp)`}
          onClick={() => documentDialog.open("create")}
          onMouseEnter={preloadOcrEngine}
          icon={<FilesIcon aria-hidden size={19} weight="duotone" />}
        />
      </div>
    </article>
  );
};
